import React, { type LegacyRef, useRef } from "react";
import { type VideoTypes, VideoPlayer } from "@bloomberg/media-ui-av";
import type { VideoPlayerProps } from "@bloomberg/media-ui-av/dist/VideoPlayer/types";

import useVideoViewportAutoplay from "~components/shared/video-player/useVideoViewportAutoplay";
import type { LineupVideoPlayerProps } from "~components/shared/video-player/VideoPlayerProvider";
import { TranslatedMessage } from "~i18n/translate";
import type { Image, Video } from "~types/types";
import isEnabled from "~utils/features";
import sizeImage, { DEFAULT_IMAGE } from "~utils/sizeImage";

import { useVideoPlayer } from "./useVideoPlayer";
import styles from "./styles.module.css";

export interface LineupVideoVodPlayerProps extends LineupVideoPlayerProps {
    video: Video;
    image?: Image;
    showCaption?: boolean;
    credit?: string;
    videoAspectRatio?: string;
    fillContainer?: boolean;
    fillPosition?: string;
    useRetinaImages?: boolean;
    desktopMaxWidth?: number;
    mobileMaxWidth?: number;
    intrinsicAspectRatio?: string;
    shareUrl?: string;
    isVerticalVideo?: boolean;
}

const VERTICAL_VIDEO_OPTION_OVERRIDES: Partial<VideoTypes.CombinedPlaylistPlayerOptions> =
    {
        use_title_bar: true,
    };

const isVideoViewportAutoplay = isEnabled("videoViewportAutoplay");

export const LineupVideoVodPlayer = React.forwardRef<
    HTMLElement,
    LineupVideoVodPlayerProps
>(function LineupVideoVodPlayerComponent(
    props: LineupVideoVodPlayerProps,
    ref: React.ForwardedRef<HTMLElement>
) {
    const {
        image,
        video,
        onError,
        showCaption = true,
        credit,
        videoAspectRatio = "16:9",
        fillContainer,
        fillPosition,
        intrinsicAspectRatio,
        shareUrl,
        isVerticalVideo = false,
    } = props;

    const {
        autoplay,
        env,
        options,
        foundationLayout,
        preloadThreshold,
        repeat,
        startFrom,
        startMuted,
    } = useVideoPlayer();

    const { title, bmmrId, url, thumbnail } = video;

    const poster = sizeImage(
        image?.baseUrl ?? thumbnail?.baseUrl ?? DEFAULT_IMAGE,
        640
    );
    const playerRef = useRef<VideoTypes.MediaPlayer>(null);

    let intersectionObserverTracker;
    let onReady;
    if (isVideoViewportAutoplay) {
        ({ intersectionObserverTracker, onReady } =
            // the value isVideoViewportAutoplay is set on build time, so it is constant
            // eslint-disable-next-line react-hooks/rules-of-hooks
            useVideoViewportAutoplay(playerRef));
    }
    const playerOptions: VideoPlayerProps["options"] = {
        ...options,
        aspectRatio: videoAspectRatio,
        fillContainer,
        fillPosition,
        id: bmmrId || "",
        poster,
        repeat: true,
        autoplay,
        useFoundationLayout: true,
        pauseOnBlur: false,
        use_share_overlay: true,
        use_google_tag_manager: true,
        serviceEnv: "production",
        intrinsicAspectRatio,
        share_metadata: shareUrl ? { canonical_url: shareUrl } : undefined,
    };

    if (isVerticalVideo) {
        Object.assign(playerOptions, VERTICAL_VIDEO_OPTION_OVERRIDES);
    }

    const caption =
        (credit ? <p className={styles.caption}> {credit} </p> : "") ||
        (title && !isVerticalVideo && title.trim() !== "" ? (
            <p className={styles.caption}>
                <span className={styles.boldPrepend}>
                    <TranslatedMessage
                        id="watch"
                        defaultMessage="Watch: {title}"
                        values={{ title }}
                    />
                </span>
            </p>
        ) : (
            ""
        ));

    return (
        <div
            className={styles.LineUpVideoPlayer}
            data-component="lineup-video-player"
        >
            <div
                className={
                    videoAspectRatio === "2:3"
                        ? styles.verticalVideoPlayerContainer
                        : styles.videoPlayerContainer
                }
                data-bmmrid={bmmrId}
                data-url={url}
                data-autoplay={autoplay}
                data-foundation
                data-repeat={false}
                data-index={0}
                data-video-module="homepage_vod"
                data-module-ad-player
                data-use-title-bar={false}
                data-poster={poster}
                ref={intersectionObserverTracker as LegacyRef<HTMLDivElement>}
            >
                <VideoPlayer
                    autoplay={autoplay}
                    env={env}
                    foundationLayout={foundationLayout}
                    onError={onError}
                    onReady={onReady ?? (() => 0)}
                    options={playerOptions}
                    playerRef={playerRef}
                    preloadThreshold={preloadThreshold}
                    ref={ref}
                    repeat={repeat}
                    startFrom={startFrom}
                    startMuted={startMuted}
                    className={styles.videoPlayer}
                />
            </div>
            {showCaption && caption}
        </div>
    );
});
